import { useState, useEffect } from 'react';
import { toast } from '@/components/ui/sonner';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { 
  Truck, Calendar, Save, ArrowLeft, Trash2, FileText, 
  AlertTriangle, CheckCircle, Clock, Shield, Wrench, FileCheck
} from 'lucide-react';
import axios from 'axios';
import { API } from '@/App';
import Layout from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';

const AdminVehicleDetails = ({ user, activeModule = "b2b" }) => {
  const { vehicleId } = useParams();
  const navigate = useNavigate();
  const [vehicle, setVehicle] = useState(null);
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [alerts, setAlerts] = useState([]);
  const [formData, setFormData] = useState({
    plate_number: '',
    brand: '',
    model: '',
    status: 'available',
    site_id: '',
    // Antipollution
    antipollution_start: '',
    antipollution_end: '',
    // Carte grise provisoire
    carte_grise_start: '',
    carte_grise_end: '',
    // Assurance
    assurance_start: '',
    assurance_end: '',
    // Contrôle technique
    controle_technique_start: '',
    controle_technique_end: '',
    // Révision
    derniere_revision: '',
    notes: ''
  });

  useEffect(() => {
    fetchVehicle();
    fetchSites();
  }, [vehicleId]);

  const fetchSites = async () => {
    try {
      const res = await axios.get(`${API}/sites`, { withCredentials: true });
      setSites(res.data.sites || res.data || []);
    } catch (error) {
      console.error('Failed to fetch sites:', error);
    }
  };

  const fetchVehicle = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/vehicles/${vehicleId}`, { withCredentials: true });
      const v = response.data;
      setVehicle(v);
      setFormData({
        plate_number: v.plate_number || '',
        brand: v.brand || '',
        model: v.model || '',
        status: v.status || 'available',
        site_id: v.site_id || '',
        antipollution_start: v.antipollution_start || '',
        antipollution_end: v.antipollution_end || '',
        carte_grise_start: v.carte_grise_start || '',
        carte_grise_end: v.carte_grise_end || '',
        assurance_start: v.assurance_start || '',
        assurance_end: v.assurance_end || '',
        controle_technique_start: v.controle_technique_start || '',
        controle_technique_end: v.controle_technique_end || '',
        derniere_revision: v.derniere_revision || '',
        notes: v.notes || ''
      });
      
      // Calculate alerts for this vehicle
      calculateAlerts(v);
    } catch (error) {
      console.error('Failed to fetch vehicle:', error);
      navigate('/admin/vehicles');
    } finally {
      setLoading(false);
    }
  };

  const calculateAlerts = (v) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const alertList = [];
    const ALERT_DAYS = 14;

    const checkDate = (endDate, type, label) => {
      if (!endDate) return null;
      const end = new Date(endDate);
      const diffDays = Math.ceil((end - today) / (1000 * 60 * 60 * 24));
      
      if (diffDays < 0) {
        return { type, label, status: 'expired', days: Math.abs(diffDays), date: endDate };
      } else if (diffDays <= ALERT_DAYS) {
        return { type, label, status: 'warning', days: diffDays, date: endDate };
      }
      return { type, label, status: 'ok', days: diffDays, date: endDate };
    };

    // Check all dates
    const antipollution = checkDate(v.antipollution_end, 'antipollution', 'Antipollution');
    if (antipollution) alertList.push(antipollution);

    const carteGrise = checkDate(v.carte_grise_end, 'carte_grise', 'Carte grise provisoire');
    if (carteGrise) alertList.push(carteGrise);

    const assurance = checkDate(v.assurance_end, 'assurance', 'Assurance');
    if (assurance) alertList.push(assurance);

    const controleTechnique = checkDate(v.controle_technique_end, 'controle_technique', 'Contrôle technique');
    if (controleTechnique) alertList.push(controleTechnique);

    // Révision: calculate next date (3 months after last)
    if (v.derniere_revision) {
      const lastRev = new Date(v.derniere_revision);
      const nextRev = new Date(lastRev);
      nextRev.setMonth(nextRev.getMonth() + 3);
      const revision = checkDate(nextRev.toISOString().split('T')[0], 'revision', 'Révision/Vidange');
      if (revision) {
        revision.calculatedDate = nextRev.toISOString().split('T')[0];
        alertList.push(revision);
      }
    }

    setAlerts(alertList);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/vehicles/${vehicleId}`, formData, { withCredentials: true });
      alert('Véhicule mis à jour avec succès !');
      fetchVehicle(); // Refresh to update alerts
    } catch (error) {
      console.error('Failed to update vehicle:', error);
      alert(error.response?.data?.detail || 'Erreur lors de la mise à jour');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!confirm('Êtes-vous sûr de vouloir supprimer ce véhicule ?')) return;
    try {
      await axios.delete(`${API}/vehicles/${vehicleId}`, { withCredentials: true });
      navigate('/admin/vehicles');
    } catch (error) {
      console.error('Failed to delete vehicle:', error);
      toast.error(`Véhicule non supprimé : ${typeof error.response?.data?.detail === 'string' ? error.response.data.detail : 'vérifiez la connexion puis réessayez'}`);
    }
  };

  const getAlertBadge = (alert) => {
    if (!alert) return null;
    
    if (alert.status === 'expired') {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 bg-red-100 text-red-700 rounded-full text-xs font-medium">
          <AlertTriangle className="w-3 h-3" />
          Expiré ({alert.days}j)
        </span>
      );
    } else if (alert.status === 'warning') {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 bg-orange-100 text-orange-700 rounded-full text-xs font-medium">
          <Clock className="w-3 h-3" />
          {alert.days}j restants
        </span>
      );
    } else if (alert.status === 'ok') {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">
          <CheckCircle className="w-3 h-3" />
          Valide ({alert.days}j)
        </span>
      );
    }
    return null;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('fr-FR');
  };

  if (loading) {
    return (
      <Layout user={user} activeModule={activeModule}>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0F172A]"></div>
        </div>
      </Layout>
    );
  }

  if (!vehicle) {
    return (
      <Layout user={user} activeModule={activeModule}>
        <div className="text-center py-12">
          <p className="text-gray-500">Véhicule non trouvé</p>
        </div>
      </Layout>
    );
  }

  // Get alerts for each type
  const getAlertForType = (type) => alerts.find(a => a.type === type);

  return (
    <Layout user={user} activeModule={activeModule}>
      <div className="space-y-6 fade-in max-w-5xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/admin/vehicles">
              <Button variant="outline" size="sm">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Retour
              </Button>
            </Link>
            <div>
              <h1 className="text-2xl font-bold text-[#0F172A] font-['Chivo'] flex items-center gap-2">
                <Truck className="w-6 h-6" />
                {vehicle.plate_number}
              </h1>
              <p className="text-gray-500">{vehicle.brand} {vehicle.model}</p>
            </div>
          </div>
          <Button
            onClick={handleDelete}
            variant="outline"
            className="text-red-600 border-red-300 hover:bg-red-50"
          >
            <Trash2 className="w-4 h-4 mr-2" />
            Supprimer
          </Button>
        </div>

        {/* Alerts Summary */}
        {alerts.some(a => a.status === 'expired' || a.status === 'warning') && (
          <Card className="border-red-200 bg-red-50">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-6 h-6 text-red-500 flex-shrink-0" />
                <div>
                  <h3 className="font-semibold text-red-800">Alertes véhicule</h3>
                  <div className="mt-2 space-y-1">
                    {alerts.filter(a => a.status === 'expired').map((a, i) => (
                      <p key={i} className="text-sm text-red-700">
                        ❌ <strong>{a.label}</strong> expiré depuis {a.days} jour(s)
                      </p>
                    ))}
                    {alerts.filter(a => a.status === 'warning').map((a, i) => (
                      <p key={i} className="text-sm text-orange-700">
                        ⚠️ <strong>{a.label}</strong> expire dans {a.days} jour(s)
                      </p>
                    ))}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Main Form */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Truck className="w-5 h-5" />
              Informations générales
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid md:grid-cols-4 gap-4">
              <div>
                <Label className="mb-2">Plaque d&apos;immatriculation *</Label>
                <Input
                  value={formData.plate_number}
                  onChange={(e) => setFormData(prev => ({ ...prev, plate_number: e.target.value.toUpperCase() }))}
                  placeholder="AB-123-CD"
                  required
                />
              </div>
              <div>
                <Label className="mb-2">Marque *</Label>
                <Input
                  value={formData.brand}
                  onChange={(e) => setFormData(prev => ({ ...prev, brand: e.target.value }))}
                  placeholder="Renault"
                  required
                />
              </div>
              <div>
                <Label className="mb-2">Modèle *</Label>
                <Input
                  value={formData.model}
                  onChange={(e) => setFormData(prev => ({ ...prev, model: e.target.value }))}
                  placeholder="Master"
                  required
                />
              </div>
              <div>
                <Label className="mb-2">Statut *</Label>
                <select
                  value={formData.status || 'available'}
                  onChange={(e) => setFormData(prev => ({ ...prev, status: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#0F172A]"
                >
                  <option value="available">Disponible</option>
                  <option value="in_use">En service</option>
                  <option value="maintenance">Maintenance</option>
                  <option value="inactive">Inactif</option>
                </select>
              </div>
            </div>
            
            {/* Site affecté */}
            <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
              <Label className="mb-2 text-blue-800 font-semibold">📍 Site affecté</Label>
              <select
                value={formData.site_id || ''}
                onChange={(e) => setFormData(prev => ({ ...prev, site_id: e.target.value }))}
                className="w-full px-3 py-2 border border-blue-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white mt-2"
              >
                <option value="">Aucun (flotte commune)</option>
                {sites.map(site => (
                  <option key={site.site_id} value={site.site_id}>{site.name}</option>
                ))}
              </select>
              <p className="text-xs text-blue-600 mt-1">Optionnel : Affectez ce véhicule à un site spécifique</p>
            </div>
          </CardContent>
        </Card>

        {/* Dates Réglementaires */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="w-5 h-5" />
              Dates réglementaires
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Antipollution */}
            <div className="p-4 rounded-lg border bg-gray-50">
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-semibold flex items-center gap-2">
                  <Shield className="w-4 h-4 text-green-600" />
                  Antipollution
                </h4>
                {getAlertBadge(getAlertForType('antipollution'))}
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm text-gray-600">Date de début</Label>
                  <Input
                    type="date"
                    value={formData.antipollution_start}
                    onChange={(e) => setFormData(prev => ({ ...prev, antipollution_start: e.target.value }))}
                  />
                </div>
                <div>
                  <Label className="text-sm text-gray-600">Date de fin</Label>
                  <Input
                    type="date"
                    value={formData.antipollution_end}
                    onChange={(e) => setFormData(prev => ({ ...prev, antipollution_end: e.target.value }))}
                  />
                </div>
              </div>
            </div>

            {/* Carte Grise Provisoire */}
            <div className="p-4 rounded-lg border bg-gray-50">
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-semibold flex items-center gap-2">
                  <FileCheck className="w-4 h-4 text-blue-600" />
                  Carte grise provisoire
                </h4>
                {getAlertBadge(getAlertForType('carte_grise'))}
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm text-gray-600">Date de début</Label>
                  <Input
                    type="date"
                    value={formData.carte_grise_start}
                    onChange={(e) => setFormData(prev => ({ ...prev, carte_grise_start: e.target.value }))}
                  />
                </div>
                <div>
                  <Label className="text-sm text-gray-600">Date de fin</Label>
                  <Input
                    type="date"
                    value={formData.carte_grise_end}
                    onChange={(e) => setFormData(prev => ({ ...prev, carte_grise_end: e.target.value }))}
                  />
                </div>
              </div>
            </div>

            {/* Assurance */}
            <div className="p-4 rounded-lg border bg-gray-50">
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-semibold flex items-center gap-2">
                  <Shield className="w-4 h-4 text-purple-600" />
                  Assurance
                </h4>
                {getAlertBadge(getAlertForType('assurance'))}
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm text-gray-600">Date de début</Label>
                  <Input
                    type="date"
                    value={formData.assurance_start}
                    onChange={(e) => setFormData(prev => ({ ...prev, assurance_start: e.target.value }))}
                  />
                </div>
                <div>
                  <Label className="text-sm text-gray-600">Date de fin</Label>
                  <Input
                    type="date"
                    value={formData.assurance_end}
                    onChange={(e) => setFormData(prev => ({ ...prev, assurance_end: e.target.value }))}
                  />
                </div>
              </div>
            </div>

            {/* Contrôle Technique */}
            <div className="p-4 rounded-lg border bg-gray-50">
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-semibold flex items-center gap-2">
                  <FileCheck className="w-4 h-4 text-orange-600" />
                  Contrôle technique
                </h4>
                {getAlertBadge(getAlertForType('controle_technique'))}
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm text-gray-600">Date de début</Label>
                  <Input
                    type="date"
                    value={formData.controle_technique_start}
                    onChange={(e) => setFormData(prev => ({ ...prev, controle_technique_start: e.target.value }))}
                  />
                </div>
                <div>
                  <Label className="text-sm text-gray-600">Date de fin</Label>
                  <Input
                    type="date"
                    value={formData.controle_technique_end}
                    onChange={(e) => setFormData(prev => ({ ...prev, controle_technique_end: e.target.value }))}
                  />
                </div>
              </div>
            </div>

            {/* Révision/Vidange */}
            <div className="p-4 rounded-lg border bg-gray-50">
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-semibold flex items-center gap-2">
                  <Wrench className="w-4 h-4 text-gray-600" />
                  Révision / Vidange
                </h4>
                {getAlertBadge(getAlertForType('revision'))}
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm text-gray-600">Date de dernière révision</Label>
                  <Input
                    type="date"
                    value={formData.derniere_revision}
                    onChange={(e) => setFormData(prev => ({ ...prev, derniere_revision: e.target.value }))}
                  />
                </div>
                <div>
                  <Label className="text-sm text-gray-600">Prochaine révision (calculée automatiquement)</Label>
                  <div className="flex items-center h-10 px-3 bg-gray-100 border border-gray-300 rounded-md">
                    {formData.derniere_revision ? (
                      <span className="text-gray-700">
                        {(() => {
                          const d = new Date(formData.derniere_revision);
                          d.setMonth(d.getMonth() + 3);
                          return formatDate(d.toISOString().split('T')[0]);
                        })()}
                        <span className="text-gray-500 text-sm ml-2">(+3 mois)</span>
                      </span>
                    ) : (
                      <span className="text-gray-400">-</span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Notes */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Notes
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Textarea
              value={formData.notes}
              onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
              placeholder="Notes diverses sur le véhicule (historique, remarques...)"
              rows={4}
            />
          </CardContent>
        </Card>

        {/* Action Buttons */}
        <div className="flex gap-3">
          <Button 
            onClick={handleSave} 
            disabled={saving}
            className="btn-primary flex-1"
          >
            {saving ? (
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                Enregistrer les modifications
              </>
            )}
          </Button>
          <Link to="/admin/vehicles" className="flex-1">
            <Button variant="outline" className="w-full">
              Annuler
            </Button>
          </Link>
        </div>
      </div>
    </Layout>
  );
};

export default AdminVehicleDetails;
